import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';

/**
 * The way back out of an edit — GEN-43.
 *
 * Buttons as well as the shortcut, because a control that exists only as a key combination
 * is a control most reviewers never learn they have. The shortcut is what they reach for
 * once they know.
 */

/** Whether this keystroke is the platform's undo, and whose it is. */
function undoIntent(event: KeyboardEvent): 'undo' | 'redo' | undefined {
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z')
    return undefined;

  return event.shiftKey ? 'redo' : 'undo';
}

/**
 * A field's own undo wins while it has focus.
 *
 * Inside a text box the platform already means "take back that keystroke", and stealing it
 * to revert a whole committed field is the more destructive reading of the same gesture —
 * a reviewer mid-sentence would lose the sentence and a previous save at once.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA'
  );
}

export function UndoRedo({
  undoLabel,
  redoLabel,
  disabled,
  scope = 'One edit at a time, across this lesson',
  onUndo,
  onRedo,
}: {
  /** The field an undo would step back, absent when there is nothing behind. */
  undoLabel: string | undefined;
  redoLabel: string | undefined;
  disabled: boolean;
  /** What the stack covers. A prop since GEN-70, when one of them spanned lessons. */
  scope?: string;
  onUndo: () => void;
  onRedo: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const intent = undoIntent(event);
      if (intent === undefined || isTyping(event.target)) return;

      const [label, step] =
        intent === 'undo' ? [undoLabel, onUndo] : [redoLabel, onRedo];

      if (disabled || label === undefined) return;

      event.preventDefault();
      step();
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [undoLabel, redoLabel, disabled, onUndo, onRedo]);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Tooltip title="⌘Z">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || undoLabel === undefined}
            onClick={onUndo}
          >
            {undoLabel === undefined ? 'Undo' : `Undo ${undoLabel}`}
          </Button>
        </span>
      </Tooltip>

      <Tooltip title="⌘⇧Z">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || redoLabel === undefined}
            onClick={onRedo}
          >
            {redoLabel === undefined ? 'Redo' : `Redo ${redoLabel}`}
          </Button>
        </span>
      </Tooltip>

      {/* The scope, on screen rather than learned by trying it — GEN-43 asks for this
          because "undo" over an autosaving form could plausibly mean either. */}
      <Typography variant="caption" color="text.secondary">
        {scope}
      </Typography>
    </Stack>
  );
}
